import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { arrayOf, bool, exact, number, string } from 'prop-types';
import { Onboarding } from '@templates';
import { Typography } from '@mixins';
import { theme } from '@styles';
import { routes } from '@constants';
import { ONBOARDING_STEPS } from '@constants/common';
import { SUCCESS } from '@constants/requestPhase';
import { ManageCompanyMemberModal, TeamMembersManagement } from '@organisms';
import { useDebounce } from '@hooks';
import { getCompanyMembersByAccessId, resetPhases, updateCompanyOnboarding } from '@store/company/duck';
import { ManageCompanyMembersErrorModal } from '@molecules';

const TeamOnboarding = ({ direction, companyMembers, pagination, companyName, companyStatus, activeMembers }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { updateCompanyMemberError, createCompanyMemberPhase } = useSelector(store => store.companyStore);

  const [sortingModel, setSortingModel] = useState({
    direction: null,
    sortBy: null,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    dispatch(
      getCompanyMembersByAccessId('my', {
        search: debouncedSearch,
        ...sortingModel,
      }),
    );
  }, [page, debouncedSearch, sortingModel]);

  useEffect(() => {
    if (createCompanyMemberPhase === SUCCESS) {
      dispatch(
        getCompanyMembersByAccessId('my', {
          page,
          search: debouncedSearch,
          pageSize: 8,
          ...sortingModel,
        }),
      );
    }
  }, [createCompanyMemberPhase]);

  const handleAddUser = () => {
    setIsModalOpen(true);
  };

  const handleResetState = () => {
    dispatch(resetPhases());
  };

  const handlePageChange = useCallback(value => {
    setPage(value.page + 1);
  }, []);

  const handleUserClick = id => {
    setIsModalOpen(true);
    setSelectedUserId(id);
  };

  const handleSortModelChange = sorting => {
    setSortingModel({
      orderBy: sorting[0]?.field,
      direction: sorting[0]?.sort.toUpperCase(),
    });
  };

  const handleSearch = event => {
    setSearch(event.target.value);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedUserId(null);
  };

  const selectedUser = useMemo(
    () => companyMembers.find(user => user.id === selectedUserId),
    [companyMembers, selectedUserId],
  );

  const handleBack = () => {
    history.push(routes.startOnboarding, { direction: 'back' });
  };

  const handleNavigateNext = () => {
    history.push(routes.companyLogoOnboarding, { direction: 'forward' });
  };

  const handleUpdateState = () => {
    dispatch(
      updateCompanyOnboarding({
        currentStep: ONBOARDING_STEPS.REGISTER_TEAM,
      }),
    );
  };

  const handleNext = () => {
    dispatch(
      updateCompanyOnboarding({
        currentStep: ONBOARDING_STEPS.REGISTER_TEAM,
        nextStep: ONBOARDING_STEPS.COMPANY_LOGO_ON_DOOR,
      }),
    );

    handleNavigateNext();
  };

  return (
    <Onboarding
      onSaveAndContinueLater={handleUpdateState}
      onNext={handleNext}
      direction={direction}
      title="Who's Joining You?"
      currentStep={1}
      showNavigationItems
      onBack={handleBack}>
      <Typography
        maxWidth="745px"
        variant="diatype-300"
        fontSize={[14, 16, 18, 20]}
        mb={[16, 20, 24, 30]}
        color={theme.color.mintMinded[900]}>
        Register your team members. Every team member will receive: access to the building, an entrance key or card to
        the office, and access to our member application.
      </Typography>
      <TeamMembersManagement
        withPagination={false}
        withBorders={false}
        page={page}
        onPageChange={handlePageChange}
        search={search}
        onSearch={handleSearch}
        companyMembers={companyMembers}
        pagination={pagination}
        companyName={companyName}
        companyStatus={companyStatus}
        activeMembers={activeMembers}
        onAddUser={handleAddUser}
        onUserClick={handleUserClick}
        onSortModelChange={handleSortModelChange}
      />
      {isModalOpen && (
        <ManageCompanyMemberModal user={selectedUser} onModalClose={handleModalClose} isOpen={isModalOpen} />
      )}
      {!!updateCompanyMemberError && !isModalOpen && (
        <ManageCompanyMembersErrorModal
          error={{
            title: 'Cannot edit team member',
            status: updateCompanyMemberError?.data.error.status,
            message: updateCompanyMemberError?.data.error.message,
          }}
          onModalClose={handleResetState}
          isOpen={!!updateCompanyMemberError}
        />
      )}
    </Onboarding>
  );
};

TeamOnboarding.defaultProps = {
  direction: null,
  activeMembers: 0,
  companyName: null,
};

TeamOnboarding.propTypes = {
  direction: string,
  activeMembers: number,
  companyStatus: string.isRequired,
  companyName: string,
  companyMembers: arrayOf(
    exact({
      id: number,
      imageUrl: string,
      email: string,
      firstName: string,
      lastName: string,
      isCompanyAdmin: bool,
      phone: string,
      status: string,
      isAbleToBookMeetingRoom: bool,
      isAbleToInvoice: bool,
      isAbleToPayByCard: bool,
    }),
  ).isRequired,
  pagination: exact({
    pageSize: number,
    page: number,
    cursor: number,
    total: number,
  }).isRequired,
};

export default TeamOnboarding;
